import React, { useState, useEffect, useRef } from 'react';
import { modulisticaAPI } from '../../services/modulisticaApi';
import FirmaCanvas from './FirmaCanvas';
import SelezioneRicerca from '../SelezioneRicerca';
import { PROVINCE_ITALIANE } from '../../constants/categorie';
import { validaDocumento, nascitaDalCF, luogoDalCF } from '../../utils/codiceFiscale';
import { chiediLuogo } from '../../utils/luogoNascita';
import { daChiedereAllArchivio, memoriaVerifiche } from '../../utils/verificaInArchivio';
import CampoDataNascita from '../CampoDataNascita';
import AvvisoCap from '../AvvisoCap';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/PazienteForm.css';
import '../../styles/ModulisticaWizard.css';
import RicercaComune from '../RicercaComune';
import RicercaStato from '../RicercaStato';
import CampoDocumento from '../CampoDocumento';
import api from '../../services/api';
import intestazioniTablet from '../../utils/chiaveDispositivo';
import { informativaPrivacy, conservazioneFoto } from '../../utils/informativaPrivacy';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useUscitaTablet, DOMANDA_USCITA } from '../../utils/uscitaTablet';
import { notificaErrore, notificaAvviso, notificaConferma, messaggioDa } from '../../utils/notifiche';

/**
 * ModulisticaWizard
 * Accettazione del paziente in 4 step:
 *   1. Anagrafica          2. Consenso privacy
 *   3. Consenso immagini   4. Consenso FEA
 *
 * I dati anagrafici dello step 1 precompilano in sola lettura gli step
 * successivi. Ogni step ha la propria firma. Un solo POST finale.
 *
 * Props:
 *  - onClose()
 *  - onSuccess(paziente)
 */

const PASSI = [
    { n: 1, etichetta: 'Anagrafica' },
    { n: 2, etichetta: 'Privacy' },
    { n: 3, etichetta: 'Immagini' },
    { n: 4, etichetta: 'Firma FEA' }
];

const ANAGRAFICA_INIZIALE = {
    cognome: '', nome: '', sesso: '', data_nascita: '',
    luogo_nascita: '', provincia_nascita: '', stato_nascita: '', codice_fiscale: '',
    stato_residenza: '',
    via: '', civico: '', citta: '', provincia: '', cap: '',
    telefono: '', email: ''
};

/* Chi firma per un minore. `relazione` parte da "madre" solo perche' un
   valore va scelto: non e' una presunzione, e si cambia con un tocco. */
const FIRMATARIO_INIZIALE = {
    cognome: '', nome: '', codice_fiscale: '', relazione: 'madre', altra_relazione: '',
};

const RELAZIONI = [
    { valore: 'madre', etichetta: 'Madre' },
    { valore: 'padre', etichetta: 'Padre' },
    { valore: 'tutore', etichetta: 'Tutore' },
    { valore: 'altro', etichetta: 'Altro' },
];

/** Anni compiuti a una data. Null se la data non c'e' o non si legge. */
function etaA(dataNascita, quando = new Date()) {
    if (!dataNascita) return null;
    const d = new Date(dataNascita);
    if (isNaN(d)) return null;
    let anni = quando.getFullYear() - d.getFullYear();
    const m = quando.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && quando.getDate() < d.getDate())) anni -= 1;
    return anni;
}

const PRIVACY_INIZIALE = {
    cura: null, familiari: null,
    familiare_nome: '', familiare_relazione: '', familiare_recapito: '',
    /* Due persone e non una: chi designa qualcuno per la comunicazione del
       proprio stato di salute quasi sempre ne indica due - il coniuge e un
       figlio - e con un campo solo la seconda finiva scritta a penna nel
       margine del modulo cartaceo. La seconda e' facoltativa. */
    familiare2_nome: '', familiare2_relazione: '', familiare2_recapito: '',
    /* Il medico curante non e' un consenso: e' un dato, e si chiede
       sempre. La spunta distingue «non ne ha» da «non gliel'hanno
       chiesto», che portano a comportamenti diversi allo sportello. */
    medico_curante: '', medico_curante_contatto: '', medico_curante_assente: false,
    promemoria: null, referti_email: null, marketing: null,
    // Predefinito, per un paziente NUOVO: nessuna opposizione. La
    // trasmissione al Sistema TS e' un obbligo di legge, l'opposizione e'
    // l'eccezione che il paziente sceglie: partire da false e' il
    // comportamento corretto, non una scorciatoia. Per questo non e' fra i
    // campi obbligatori. Per un paziente gia' in archivio si parte dalla
    // scheda: vedi opposizioneDellaScheda, qui sotto.
    opposizione_ts: false
};

/* L'OPPOSIZIONE AL SISTEMA TS DI UN PAZIENTE GIA' IN ARCHIVIO PARTE DALLA
   SCHEDA (Alex, 4/10/2026). Il predefinito qui sopra vale per chi una scheda
   non ce l'ha ancora. Per chi c'e' gia' - «Completa la pratica», «Fai
   rifirmare i moduli», la pratica mandata al tablet - partire da «Nessuna
   opposizione» voleva dire che chi aveva l'opposizione e rifirmava senza
   guardare quella voce la toglieva: la pratica scrive sulla scheda la scelta
   del modulo (services/opposizioneTs.registra), e decide l'ultimo modulo
   firmato. Adesso il modulo parte da cio' che dice la scheda, letto con la
   regola dell'invio (opposizione_ts_attiva, services/opposizioneTs.attiva):
   alla postazione dalla scheda aperta, al tablet dalla sessione della pratica.

   Se il valore della scheda non arriva (un backend che non lo manda), il
   modulo NON ripiega su «Nessuna opposizione»: la scelta resta da fare, come
   un consenso, e il passo non va avanti finche' non la si fa. Il ripiego
   silenzioso era proprio il difetto. */
function opposizioneDellaScheda(pazienteEsistente) {
    if (!pazienteEsistente) return null;
    const v = pazienteEsistente.opposizione_ts_attiva;
    return typeof v === 'boolean' ? v : undefined;
}

/* La riga che dice il cambio PRIMA della firma, accanto alla scelta. Due
   frasi intere e non una composta: i manuali le citano alla lettera, e
   verificaManuali le cerca cosi' come sono scritte. Le scelte fra
   virgolette sono le etichette dei due pulsanti qui sotto. */
const MSG_OPPOSIZIONE_TOLTA =
    'Stai cambiando l\'opposizione al Sistema TS: sulla scheda c\'è “Mi oppongo”, '
    + 'con questa firma diventa “Nessuna opposizione”.';
const MSG_OPPOSIZIONE_AGGIUNTA =
    'Stai cambiando l\'opposizione al Sistema TS: sulla scheda c\'è “Nessuna opposizione”, '
    + 'con questa firma diventa “Mi oppongo”.';

const FOTO_INIZIALE = {
    clinica: null, didattica: null, promozionale: null, modalita: 'volto_oscurato'
};

const FEA_INIZIALE = {
    accetta: null,
    documento: { tipo: '', numero: '', rilasciato_da: '', data_rilascio: '' }
};

/* ---------- Sotto-componenti ---------- */

/* Le etichette sono personalizzabili perche' non tutte le scelte sono
   consensi: l'opposizione al Sistema TS e' l'esercizio di un diritto, e
   scriverci "Acconsento" la renderebbe ambigua - chi clicca tutti "si"
   finirebbe per opporsi senza volerlo. */
const SceltaConsenso = ({ valore, onChange, etichette, neutro }) => {
    const testi = etichette || { si: 'Acconsento', no: 'Non acconsento' };
    // Verde e rosso dicono "scelta giusta" e "scelta sbagliata": vanno bene
    // per un consenso, non per l'opposizione al Sistema TS, dove il caso
    // normale ("Nessuna opposizione") finirebbe in rosso come un errore.
    const marca = (attiva, quale) => (attiva ? `attiva ${neutro ? 'neutra' : quale}` : '');
    return (
        <div className="scelta-consenso">
            <label className={`scelta ${marca(valore === true, 'si')}`}>
                <input type="radio" checked={valore === true} onChange={() => onChange(true)} />
                <span>{testi.si}</span>
            </label>
            <label className={`scelta ${marca(valore === false, 'no')}`}>
                <input type="radio" checked={valore === false} onChange={() => onChange(false)} />
                <span>{testi.no}</span>
            </label>
        </div>
    );
};

const VoceConsenso = ({ titolo, descrizione, valore, onChange, errore, children, etichette, neutro }) => (
    <div className={`voce-consenso ${errore ? 'errore' : ''}`}>
        <h4>{titolo}</h4>
        {descrizione && <p className="voce-descrizione">{descrizione}</p>}
        <SceltaConsenso valore={valore} onChange={onChange} etichette={etichette} neutro={neutro} />
        {children}
        {errore && <span className="messaggio-errore">{errore}</span>}
    </div>
);

const RiepilogoPaziente = ({ a }) => (
    <div className="riepilogo-paziente">
        <div className="riepilogo-nome">{a.cognome} {a.nome}</div>
        <div className="riepilogo-dettagli">
            nato/a a {a.luogo_nascita || '—'}{a.provincia_nascita ? ` (${a.provincia_nascita})` : ''}
            {' '}il {a.data_nascita ? new Date(a.data_nascita).toLocaleDateString('it-IT') : '—'}
            {' '}— C.F. {a.codice_fiscale || '—'}
        </div>
        <span className="riepilogo-nota">Dati acquisiti allo step 1</span>
    </div>
);

/* ---------- Componente principale ---------- */

const ModulisticaWizard = ({
    onClose, onSuccess, modalita = 'completo',
    // Sul tablet: la chiave del dispositivo. Se c'e', la pratica va
    // nella coda di conferma invece di creare subito il paziente.
    chiaveTablet = null,
    // Paziente gia' in archivio (importato dal vecchio gestionale):
    // i campi nascono compilati e l'invio aggiorna invece di creare.
    pazienteEsistente = null,
    /* Il codice della sessione, quando la pratica di un paziente
       esistente si firma al tablet: e' cio' che autorizza la scrittura,
       insieme alla chiave del dispositivo. */
    codiceSessione = null,
    /* La compilazione lasciata a meta', quando la segreteria ha premuto
       «Riprendi». Arriva dal server, non dal browser: il tablet non
       conserva niente di suo, cosi' un apparecchio ripreso in mano da
       un'altra persona non ha in memoria quello di prima. */
    bozza = null,
}) => {
    // In modalita 'prelievi' il paziente firma solo Anagrafica, Privacy e FEA:
    // niente consenso immagini, niente consenso alla cura (specifici delle
    // prestazioni). I passi attivi guidano navigazione, stepper e validazione.
    const soloPrelievi = modalita === 'prelievi';
    const passiAttivi = soloPrelievi ? [1, 2, 4] : [1, 2, 3, 4];
    const ultimoPasso = passiAttivi[passiAttivi.length - 1];

    const [passo, setPasso] = useState(bozza?.passo || 1);

    /* Dal paziente esistente si prende quello che c'e': i campi vuoti
       restano vuoti e li completa lui. */
    const datiIniziali = pazienteEsistente
        ? {
            ...ANAGRAFICA_INIZIALE,
            cognome: pazienteEsistente.cognome || '',
            nome: pazienteEsistente.nome || '',
            sesso: pazienteEsistente.sesso || '',
            data_nascita: (pazienteEsistente.data_nascita || '').slice(0, 10),
            luogo_nascita: pazienteEsistente.luogo_nascita || '',
            provincia_nascita: pazienteEsistente.provincia_nascita || '',
            stato_nascita: pazienteEsistente.stato_nascita || '',
            codice_fiscale: pazienteEsistente.codice_fiscale || '',
            tipo_documento: pazienteEsistente.tipo_documento || 'cf_italiano',
            via: pazienteEsistente.via || '',
            civico: pazienteEsistente.civico || '',
            citta: pazienteEsistente.citta || '',
            provincia: pazienteEsistente.provincia || '',
            cap: pazienteEsistente.cap || '',
            /* Senza questa riga chi risiede all'estero si ritrovava il
               campo vuoto, cioe' «Italia», e il modulo tornava a
               pretendergli provincia e CAP italiani: il caso che si era
               risolto il 4 settembre si ripresentava a ogni riapertura. */
            stato_residenza: pazienteEsistente.stato_residenza || '',
            telefono: pazienteEsistente.telefono || '',
            email: pazienteEsistente.email || '',
        }
        : ANAGRAFICA_INIZIALE;

    /* La bozza vince sui dati iniziali: e' quello che la persona aveva
       gia' scritto, ed e' il motivo per cui la si riprende. */
    const [anagrafica, setAnagrafica] = useState(bozza?.anagrafica || datiIniziali);

    /* Vuoto significa Italia: e' la stessa convenzione della colonna in
       database, dove scrivere 'Italia' su ventiseimila righe importate
       avrebbe affermato un dato che nessuno ha verificato.

       DEVE STARE DOPO `anagrafica`, NON PRIMA. Scritto sopra la useState
       leggeva una const non ancora inizializzata: non un valore vuoto, un
       ReferenceError a ogni render. Il wizard non si apriva piu' - ne' alla
       postazione ne' al tablet - e la build passava lo stesso, perche' e'
       un errore di esecuzione e non di sintassi. */
    const residenzaInItalia = !String(anagrafica.stato_residenza || '').trim()
        || /^ital/i.test(String(anagrafica.stato_residenza).trim());

    const [dichiarazione, setDichiarazione] = useState(bozza?.dichiarazione || false);
    /* Per un paziente gia' in archivio la scelta sul Sistema TS parte dalla
       scheda (vedi opposizioneDellaScheda); se il valore non e' arrivato, da
       nessuna scelta (null). La bozza vince anche qui: e' quello che la
       persona aveva gia' scelto, e se differisce dalla scheda la riga del
       cambio lo dice. */
    const oppostoSullaScheda = opposizioneDellaScheda(pazienteEsistente);
    const [privacy, setPrivacy] = useState(bozza?.privacy || (pazienteEsistente
        ? { ...PRIVACY_INIZIALE, opposizione_ts: oppostoSullaScheda === undefined ? null : oppostoSullaScheda }
        : PRIVACY_INIZIALE));
    const [foto, setFoto] = useState(bozza?.foto || FOTO_INIZIALE);
    const [fea, setFea] = useState(bozza?.fea || FEA_INIZIALE);
    const [firme, setFirme] = useState({ anagrafica: null, privacy: null, foto: null, fea: null });

    /* «Vuoi uscire senza salvare» - ma NON sul tablet. Alla postazione
       chi chiude per sbaglio una pratica a meta' perde anagrafica e
       spunte, e la domanda lo ferma; sul tablet la finestra e' in mano
       al paziente, che quella domanda non saprebbe interpretare, e la
       protezione e' lo stato della sessione visto dalla segreteria
       (utils/uscitaProtetta.js). I dati iniziali vengono dalle props,
       non da un caricamento: l'istantanea si prende al montaggio. */
    /* `versioneIniziale` rifa' l'istantanea dopo la proposta del luogo fatta
       all'apertura (precompilaLuogo, qui sotto): quella la fa il modulo da
       se', e una pratica aperta e chiusa senza toccare niente non deve
       chiedere «vuoi uscire senza salvare» (revisione del 3/10/2026: sulla
       copia, circa 2.500 pazienti con la Z e senza Stato). Una domanda che
       compare quando va tutto bene insegna a premere OK senza leggere. */
    const [versioneIniziale, setVersioneIniziale] = useState(0);
    const sporco = useSporco(
        chiaveTablet ? null : { anagrafica, dichiarazione, privacy, foto, fea, firme },
        versioneIniziale
    );
    const chiedi = useUscitaProtetta(sporco);

    /* AL TABLET C'E' QUALCOSA DA PERDERE, E NESSUNO LO CHIEDEVA.
       `useSporco` riceve `null` quando si e' al tablet, quindi `chiedi()`
       diceva sempre di si' e «Annulla» e «✕» buttavano via tutto senza
       una parola. Il 23 settembre 2026 due pazienti anziani hanno perso
       la compilazione a meta'.
       Qui «in corso» non e' un confronto con un'istantanea: e' «ha
       scritto qualcosa». Al tablet il modulo nasce vuoto o con i dati di
       un paziente in archivio, e quello che conta e' se la persona ha
       messo del suo. */
    const inCorso = chiaveTablet
        ? (passo > 1 || Object.values(anagrafica).some((v) => String(v || '').trim() !== ''))
        : false;
    const chiudi = () => {
        if (chiaveTablet) {
            if (inCorso && !window.confirm(DOMANDA_USCITA)) return;
            return onClose();
        }
        if (chiedi()) onClose();
    };
    /* Chi firma, quando non e' il paziente. Per un minore firma chi ne
       esercita la responsabilita' genitoriale, e sul modulo deve
       risultare chi e': una firma senza un nome accanto non dice a nessuno
       chi ha acconsentito. */
    const [firmatario, setFirmatario] = useState(bozza?.firmatario || FIRMATARIO_INIZIALE);

    /* Minore alla data della firma, non alla data di importazione: chi
       compila oggi un modulo per chi ha compiuto diciotto anni la
       settimana scorsa firma da se'. */
    const anni = etaA(anagrafica.data_nascita);
    const minore = anni !== null && anni < 18;
    const etichettaFirma = minore
        ? 'Firma di chi esercita la responsabilità genitoriale'
        : undefined;
    const [errori, setErrori] = useState({});
    const [invio, setInvio] = useState(false);
    /* Chi e' il titolare del trattamento lo dice il server. La rotta non e'
       autenticata perche' questa schermata gira anche sul tablet. */
    const [titolare, setTitolare] = useState('');
    /* Gli anni di conservazione delle fotografie: dal server come il
       titolare, e per la stessa ragione - su un'installazione di
       Coorte il numero e' un altro. */
    const [anniFoto, setAnniFoto] = useState(10);
    useEffect(() => {
        fetch('/api/struttura')
            .then((r) => r.json())
            .then((d) => {
                setTitolare(d?.data?.titolare_esteso || '');
                if (d?.data?.anni_conservazione_foto) setAnniFoto(d.data.anni_conservazione_foto);
            })
            .catch(() => {});
    }, []);

    /* ---------- Aggiornamento campi ---------- */

    const aggiornaAnagrafica = (campo, valore) => {
        let v = valore;
        if (campo === 'codice_fiscale') v = valore.toUpperCase().replace(/\s/g, '');
        if (campo === 'provincia' || campo === 'provincia_nascita') v = valore.toUpperCase().slice(0, 2);
        /* Il CAP italiano sono cinque cifre; quello estero si tiene PER
           INTERO («SW1A 1AA»). Fino al 24/09/2026 qui si tenevano solo le
           cifre, tagliate a cinque, anche per chi risiede all'estero: il
           campo lasciava scrivere dodici caratteri e ne salvava due, in
           silenzio (trovato dal server beta). */
        if (campo === 'cap') {
            v = residenzaInItalia
                ? valore.replace(/\D/g, '').slice(0, 5)
                : valore.toUpperCase().replace(/[^A-Z0-9 -]/g, '').slice(0, 12);
        }
        setAnagrafica((p) => ({ ...p, [campo]: v }));
        pulisciErrore(campo);
    };

    /* DATA E SESSO DAL CODICE FISCALE (Alex, 26/09/2026): con un codice
       italiano valido si precompilano, se sono ancora vuoti, e il modulo
       dice di controllarli. Una data gia' scritta non si sovrascrive: se
       non corrisponde, il campo del codice lo segnala come avviso. */
    const [daCodice, setDaCodice] = useState(false);

    /* IL LUOGO DAL CODICE FISCALE (3/10/2026, ramo cf-estero). Le posizioni
       12-15 dicono dove si e' nati: un comune, uno Stato estero (la Z) o un
       comune ceduto nel 1947. Con un codice italiano valido il modulo propone
       lo Stato di nascita (e la provincia «EE», la convenzione della colonna)
       oppure il comune, SE il campo e' vuoto: quello che la persona ha gia'
       scritto non si tocca. Al server va solo il codice del luogo, mai il
       codice fiscale (utils/luogoNascita.js). `proposto` ricorda che cosa ha
       scritto il modulo da se': se il codice cambia, la proposta di prima si
       toglie, ma solo se nessuno l'ha cambiata nel frattempo. */
    const [luogoDalCodice, setLuogoDalCodice] = useState(null);
    const luogoChiesto = useRef('');
    const proposto = useRef({});
    const precompilaLuogo = async (cf, { apertura = false } = {}) => {
        const l = luogoDalCF(cf);
        luogoChiesto.current = l ? l.codice : '';
        const d = l ? await chiediLuogo(l.codice, { chiave: chiaveTablet }) : null;
        if (luogoChiesto.current !== (l ? l.codice : '')) return;   // il codice e' cambiato nel frattempo
        const luogo = d && d.tipo !== 'sconosciuto' ? d : null;
        setLuogoDalCodice(luogo);
        setAnagrafica((p) => {
            const q = { ...p };
            /* via la proposta di prima, se e' ancora com'era */
            for (const [campo, valore] of Object.entries(proposto.current)) {
                if (q[campo] === valore) q[campo] = '';
            }
            const nuova = {};
            if (luogo && luogo.tipo === 'stato') {
                if (!String(q.stato_nascita || '').trim()) nuova.stato_nascita = luogo.nome;
                if (!String(q.provincia_nascita || '').trim()) nuova.provincia_nascita = 'EE';
            } else if (luogo && (luogo.tipo === 'comune' || luogo.tipo === 'ceduto')) {
                /* Un comune ceduto nel 1947 (Alex, 3/10/2026, come proposto):
                   il nome italiano con la sigla della provincia di ALLORA
                   (Pola, PL), e mai lo Stato di oggi - legge 54 del 1989. */
                if (!String(q.luogo_nascita || '').trim()) nuova.luogo_nascita = luogo.nome;
                if (!String(q.provincia_nascita || '').trim() && luogo.sigla_provincia) {
                    nuova.provincia_nascita = luogo.sigla_provincia;
                }
            }
            proposto.current = nuova;
            return { ...q, ...nuova };
        });
        /* All'apertura la proposta fa parte della pratica com'e' arrivata:
           l'istantanea si rifa' nello stesso giro di aggiornamento (React 18
           li raccoglie insieme anche dopo un await). Le proposte fatte mentre
           si scrive il codice restano modifiche vere. */
        if (apertura) setVersioneIniziale((v) => v + 1);
        pulisciErrore('luogo_nascita');
        pulisciErrore('provincia_nascita');
    };

    /* Un paziente gia' in archivio (o una bozza ripresa) arriva col codice
       gia' scritto: il luogo si propone all'apertura. E' il caso dei 2.528
       pazienti con la Z e senza Stato di nascita (copia, 3/10/2026). */
    useEffect(() => {
        if ((anagrafica.tipo_documento || 'cf_italiano') === 'cf_italiano' && anagrafica.codice_fiscale) {
            precompilaLuogo(anagrafica.codice_fiscale, { apertura: true });
        }
    }, []);

    const precompilaDalCodice = (cf) => {
        if ((anagrafica.tipo_documento || 'cf_italiano') !== 'cf_italiano') return;
        precompilaLuogo(cf);
        const n = nascitaDalCF(cf);
        if (!n || (anagrafica.data_nascita && anagrafica.sesso)) return;
        setAnagrafica((p) => ({
            ...p,
            data_nascita: p.data_nascita || n.data_nascita,
            sesso: p.sesso || n.sesso,
        }));
        setDaCodice(true);
        pulisciErrore('data_nascita');
        pulisciErrore('sesso');
    };

    const aggiornaDocumento = (campo, valore) => {
        setFea((p) => ({ ...p, documento: { ...p.documento, [campo]: valore } }));
        pulisciErrore('documento');
    };

    const pulisciErrore = (campo) => {
        setErrori((p) => (p[campo] ? { ...p, [campo]: null } : p));
    };

    const impostaFirma = (chiave, dataUrl) => {
        setFirme((p) => ({ ...p, [chiave]: dataUrl }));
        pulisciErrore('firma_' + chiave);
    };

    /* ---------- Validazione per step ---------- */


    /* Con 26.000 anagrafiche importate, molti di quelli che si
       presentano all'accettazione come nuovi sono gia' in archivio.
       Non si blocca nulla: si fa accorgere chi sta scrivendo. */
    const [giaInArchivio, setGiaInArchivio] = useState(null);

    /* IL TABLET NON E' RIUSCITO A CHIEDERE (2/10/2026): il limite delle
       verifiche, la rete, un errore del server. Non e' «non registrato»:
       fino a quel giorno lo diventava in silenzio, il modulo andava avanti
       e se la persona era gia' in archivio lo si scopriva solo alla
       conferma. Adesso il modulo lo dice e va avanti lo stesso: la pratica
       arriva in segreteria, e la conferma automatica ferma un doppione. */
    const [nonVerificato, setNonVerificato] = useState(false);

    /* L'ultimo codice scritto. Una risposta che arriva quando il codice e'
       gia' cambiato e' la risposta a un'altra domanda, e non si disegna. */
    const codiceScritto = useRef('');

    /* AL TABLET, UNA DOMANDA PER CODICE IN TUTTA LA COMPILAZIONE (Alex,
       2/10/2026; utils/verificaInArchivio.js). Il tablet ha cinque codici
       diversi l'ora, e il 2 ottobre un tablet ha chiesto lo stesso codice
       tre volte in tre secondi. La memoria nasce col modulo e se ne va
       con lui: la persona dopo e' un altro modulo. Ogni esito, anche il
       rifiuto, resta in memoria: lo stesso codice non si richiede. */
    const [chiediAlTablet] = useState(() => memoriaVerifiche(async (cf) => {
        try {
            /* Sul tablet risponde un paziente qualsiasi: l'endpoint
               dedicato dice solo che il codice risulta registrato, mai
               a chi appartiene. */
            const r = await fetch(
                `/api/firma-tablet/verifica-cf?cf=${encodeURIComponent(cf)}`,
                { headers: intestazioniTablet(chiaveTablet) }
            );
            if (!r.ok) return { nonVerificato: true };
            const j = await r.json();
            return { trovato: !!j?.data?.trovato };
        } catch (e) {
            return { nonVerificato: true };
        }
    }));

    const cercaInArchivio = async (cf) => {
        codiceScritto.current = cf || '';
        setNonVerificato(false);
        if (pazienteEsistente) { setGiaInArchivio(null); return; }
        if (chiaveTablet) {
            /* Solo un codice fiscale italiano col carattere di controllo
               giusto: uno sbagliato non passa comunque il primo passo, e
               chiederlo spenderebbe una verifica per niente. */
            setGiaInArchivio(null);
            if (!daChiedereAllArchivio(cf, anagrafica.tipo_documento)) return;
            const esito = await chiediAlTablet(cf);
            if (codiceScritto.current !== cf) return;
            setGiaInArchivio(esito.trovato ? { anonimo: true } : null);
            setNonVerificato(!!esito.nonVerificato);
            return;
        }
        /* Alla postazione si chiede come prima: nessun limite l'ora, e la
           risposta porta nome e stato della scheda, che possono cambiare
           mentre si compila (un paziente recuperato dal cestino). */
        if (!cf || cf.length !== 16) { setGiaInArchivio(null); return; }
        try {
            const r = await api.get('/pazienti/verifica-cf', { params: { cf } });
            if (codiceScritto.current !== cf) return;
            setGiaInArchivio(r.data.data?.trovato ? r.data.data : null);
        } catch (e) {
            if (codiceScritto.current === cf) setGiaInArchivio(null);
        }
    };


    const validaPasso1Base = () => {
        const e = {};
        const obbligatori = {
            cognome: 'Indica il cognome', nome: 'Indica il nome', sesso: 'Seleziona il sesso',
            data_nascita: 'Indica la data di nascita', luogo_nascita: 'Indica il comune di nascita',
            codice_fiscale: 'Indica il codice fiscale',
            via: 'Indica la via', civico: 'Indica il civico', citta: 'Indica la citta',
            telefono: 'Indica un recapito', email: 'Indica un indirizzo email'
        };

        /* Provincia e CAP si chiedono solo a chi risiede in Italia. A San
           Marino non c'e' nessuna provincia, e pretenderla bloccava
           l'accettazione: era il caso segnalato il 4 settembre 2026.
           Il campo che decide e' lo stato di RESIDENZA, non il tipo di
           documento e non lo stato di nascita - chi e' nato all'estero e
           vive a Rimini ha eccome una provincia. */
        if (residenzaInItalia) {
            obbligatori.provincia = 'Indica la provincia';
            obbligatori.cap = 'Indica il CAP';
        }
        // Stessa logica sulla nascita: chi e' nato all'estero non ha una
        // provincia italiana di nascita, ha uno Stato.
        /* E chi e' nato in un comune ceduto nel 1947 (Pola, Fiume, Zara) ha
           la provincia di allora, che non e' fra quelle di oggi: il modulo
           la propone (PL) e la lascia scegliere, ma non la pretende
           (3/10/2026). */
        if (!String(anagrafica.stato_nascita || '').trim()
            && !(luogoDalCodice && luogoDalCodice.tipo === 'ceduto')) {
            obbligatori.provincia_nascita = 'Indica la provincia';
        }

        Object.keys(obbligatori).forEach((c) => {
            if (!String(anagrafica[c] ?? '').trim()) e[c] = obbligatori[c];
        });

        /* Si valida secondo il TIPO di documento. Prima si pretendevano
           sedici caratteri sempre, e con il codice ISS di San Marino
           selezionato l'errore compariva due volte - una qui e una dentro
           CampoDocumento - senza nessun modo di andare avanti. */
        if (anagrafica.codice_fiscale) {
            const esito = validaDocumento(
                anagrafica.codice_fiscale,
                anagrafica.tipo_documento || 'cf_italiano',
                { data_nascita: anagrafica.data_nascita, sesso: anagrafica.sesso }
            );
            if (!esito.valido) e.codice_fiscale = esito.errore;
        }
        if (residenzaInItalia && anagrafica.cap && !/^\d{5}$/.test(anagrafica.cap))
            e.cap = 'Il CAP deve essere di 5 cifre';
        if (anagrafica.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(anagrafica.email))
            e.email = 'Formato email non valido';
        if (anagrafica.data_nascita && new Date(anagrafica.data_nascita) > new Date())
            e.data_nascita = 'La data di nascita non puo essere futura';
        if (!dichiarazione) e.dichiarazione = 'Conferma la dichiarazione per proseguire';
        if (!firme.anagrafica) e.firma_anagrafica = 'Apponi la firma nel riquadro';

        return e;
    };

    const validaPasso2 = () => {
        const e = {};
        // Il consenso alla cura riguarda le prestazioni sanitarie: non si
        // chiede a chi viene solo per il prelievo.
        if (!soloPrelievi) {
            if (privacy.cura === null) e.cura = 'Esprimi una scelta';
            else if (privacy.cura === false)
                e.cura = 'Senza questo consenso la prestazione sanitaria non puo essere erogata';
        }
        if (privacy.familiari === null) e.familiari = 'Esprimi una scelta';
        if (privacy.familiari === true && !privacy.familiare_nome.trim())
            e.familiari = 'Indica almeno la prima persona designata';
        /* Obbligatorio, ma con una via d'uscita dichiarata: chi non ha un
           medico curante lo spunta. Senza la spunta un campo vuoto
           bloccherebbe l'accettazione di chi non ne ha davvero. */
        if (!privacy.medico_curante_assente && !privacy.medico_curante.trim())
            e.medico_curante = 'Indica il medico curante, oppure spunta che il paziente non ne ha';
        if (privacy.promemoria === null) e.promemoria = 'Esprimi una scelta';
        if (privacy.referti_email === null) e.referti_email = 'Esprimi una scelta';
        if (privacy.marketing === null) e.marketing = 'Esprimi una scelta';
        /* Succede solo a un paziente in archivio di cui non e' arrivato il
           valore della scheda: meglio chiedere che ripiegare su «Nessuna
           opposizione» (vedi opposizioneDellaScheda). */
        if (typeof privacy.opposizione_ts !== 'boolean') e.opposizione_ts = 'Esprimi una scelta';
        if (!firme.privacy) e.firma_privacy = 'Apponi la firma nel riquadro';
        return e;
    };

    const validaPasso3 = () => {
        const e = {};
        if (foto.clinica === null) e.clinica = 'Esprimi una scelta';
        if (foto.didattica === null) e.didattica = 'Esprimi una scelta';
        if (foto.promozionale === null) e.promozionale = 'Esprimi una scelta';
        if (!firme.foto) e.firma_foto = 'Apponi la firma nel riquadro';
        return e;
    };

    const validaPasso4 = () => {
        const e = {};
        if (fea.accetta === null) e.accetta = 'Esprimi una scelta';
        if (fea.accetta === true) {
            if (!fea.documento.tipo.trim() || !fea.documento.numero.trim())
                e.documento = 'Indica tipo e numero del documento di identita';
        }
        if (!firme.fea) e.firma_fea = 'Apponi la firma nel riquadro';
        return e;
    };

    /* Per un minore i dati di chi firma sono obbligatori, e si controllano
       al primo passo: scoprirlo al quarto, dopo quattro firme, vorrebbe
       dire tornare indietro con il paziente che aspetta. */
    const validaFirmatario = () => {
        const e = {};
        if (!minore) return e;
        if (!firmatario.cognome.trim() || !firmatario.nome.trim())
            e.firmatario = 'Indica cognome e nome di chi firma per il minore';
        else if (firmatario.relazione === 'altro' && !firmatario.altra_relazione.trim())
            e.firmatario = 'Specifica in quale qualità firma';
        return e;
    };

    const validaPasso1 = () => ({ ...validaPasso1Base(), ...validaFirmatario() });

    const validatori = { 1: validaPasso1, 2: validaPasso2, 3: validaPasso3, 4: validaPasso4 };

    const scorriInAlto = () => {
        document.querySelector('.modulo-body')?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /* IL PASSAGGIO COMPLETATO SI METTE DA PARTE SUL SERVER.
       Una chiamata per passaggio, non per tasto: il punto non e' non
       perdere l'ultima lettera, e' non perdere venti minuti di lavoro -
       che e' quello che il 23 settembre 2026 e' successo a due pazienti
       anziani.
       LE FIRME NON CI VANNO. Se la persona esce dopo aver firmato, si
       rifirma: una firma e' un atto, e un atto non si conserva in una
       bozza per riapplicarlo dopo. Lo rifiuta anche il database
       (vincolo `bozze_senza_firme`, migration 206).
       Se il salvataggio non riesce, la compilazione VA AVANTI lo stesso:
       questa e' una rete, non un cancello, e fermare una persona davanti
       a un tablet perche' una rete non ha tenuto sarebbe peggio del
       difetto che si sta chiudendo. */
    const mettiDaParte = async (passoRaggiunto) => {
        if (!chiaveTablet) return;
        try {
            await api.post('/firma-tablet/bozza', {
                codice: codiceSessione || undefined,
                passo: passoRaggiunto,
                dati: { anagrafica, dichiarazione, privacy, foto, fea, firmatario },
            }, { headers: intestazioniTablet(chiaveTablet) });
        } catch (e) {
            /* In console, non a schermo: il paziente non sa cosa farsene
               e la segreteria vede lo stesso che la bozza non c'e'. */
            console.error('Bozza non messa da parte:', e?.message || e);
        }
    };

    const avanti = () => {
        const e = validatori[passo]();
        /* AL TABLET, UN CODICE GIA' REGISTRATO FERMA QUI.
           Prima il riquadro diceva «rivolgiti alla segreteria» e il
           pulsante Avanti restava attivo: chi non lo leggeva compilava
           tutta la pratica, la firmava, e la conferma automatica la
           rifiutava alla fine con un 409. Meglio fermarsi al primo passo
           e mandare la persona dove il problema si risolve. */
        if (chiaveTablet && giaInArchivio && giaInArchivio.anonimo) {
            e.codice_fiscale = 'Risulti già registrato. Consegna il tablet in segreteria.';
        }
        setErrori(e);
        if (Object.keys(e).length) return scorriInAlto();
        const idx = passiAttivi.indexOf(passo);
        const prossimo = passiAttivi[idx + 1];
        setPasso(prossimo);
        mettiDaParte(prossimo);
        scorriInAlto();
    };

    const indietro = () => {
        setErrori({});
        const idx = passiAttivi.indexOf(passo);
        setPasso(passiAttivi[idx - 1]);
        scorriInAlto();
    };

    /* L'indietro del browser e del sistema torna al passaggio prima
       invece di portare via dalla pagina; la chiusura della scheda
       avvisa. Il trascinamento che ricarica lo ferma `overscroll-behavior`
       in Tablet.css: da JavaScript non si ferma. */
    useUscitaTablet({
        attivo: !!chiaveTablet,
        inCorso,
        puoTornare: passiAttivi.indexOf(passo) > 0,
        indietro,
        esci: onClose,
    });

    /* ---------- Invio finale ---------- */

    const conferma = async () => {
        // Rivalida ogni passo attivo prima dell'invio: cosi' in modalita
        // prelievi non restano scoperti controlli saltati.
        let e = {};
        for (const p of passiAttivi) e = { ...e, ...validatori[p]() };
        setErrori(e);
        if (Object.keys(e).length) {
            // Torna al primo passo attivo con errori
            const primoConErrore = passiAttivi.find((p) => Object.keys(validatori[p]()).length);
            if (primoConErrore) setPasso(primoConErrore);
            return scorriInAlto();
        }

        const payload = {
            anagrafica,
            tipo_accettazione: soloPrelievi ? 'prelievi' : 'completo',
            firme,
            /* Chi ha firmato, quando non e' il paziente. Si manda solo se
               il paziente e' minore: mandarlo sempre metterebbe un
               firmatario diverso dal paziente su moduli dove non c'e'. */
            firmatario: minore ? {
                cognome: firmatario.cognome.trim(),
                nome: firmatario.nome.trim(),
                codice_fiscale: firmatario.codice_fiscale.trim() || null,
                relazione: firmatario.relazione === 'altro'
                    ? firmatario.altra_relazione.trim() : firmatario.relazione,
                per_minore: true,
                eta_paziente: anni,
            } : null,
            consensi: {
                privacy: {
                    cura: soloPrelievi ? null : privacy.cura,
                    familiari: privacy.familiari,
                    familiare_nome: privacy.familiare_nome || null,
                    familiare_relazione: privacy.familiare_relazione || null,
                    familiare_recapito: privacy.familiare_recapito || null,
                    familiare2_nome: privacy.familiare2_nome || null,
                    familiare2_relazione: privacy.familiare2_relazione || null,
                    familiare2_recapito: privacy.familiare2_recapito || null,
                    medico_curante: privacy.medico_curante || null,
                    medico_curante_contatto: privacy.medico_curante_contatto || null,
                    medico_curante_assente: privacy.medico_curante_assente === true,
                    promemoria: privacy.promemoria,
                    referti_email: privacy.referti_email,
                    marketing: privacy.marketing,
                    opposizione_ts: privacy.opposizione_ts === true
                },
                foto: soloPrelievi ? null : {
                    clinica: foto.clinica,
                    didattica: foto.didattica,
                    promozionale: foto.promozionale,
                    modalita: foto.promozionale ? foto.modalita : null
                },
                fea: {
                    accetta: fea.accetta,
                    documento: fea.documento
                }
            },
            firmato_il: new Date().toISOString(),
            user_agent: navigator.userAgent
        };

        try {
            setInvio(true);

            /* --- Paziente gia' in archivio, firmato al tablet ---
                   Va prima del ramo della postazione: al tablet non e'
                   collegato nessun utente, e la rotta autenticata di sotto
                   risponderebbe 401. Era il caso che mancava. */
            if (pazienteEsistente && chiaveTablet) {
                const r = await fetch('/api/firma-tablet/pratica-paziente', {
                    method: 'POST',
                    headers: intestazioniTablet(chiaveTablet, true),
                    body: JSON.stringify({
                        codice: codiceSessione, ...payload,
                    }),
                });
                const d = await r.json();
                if (!d.success) {
                    notificaErrore(d.message || 'Errore nell\'invio della pratica.');
                    return;
                }
                if (onSuccess) onSuccess(null);
                onClose();
                return;
            }

            /* --- Paziente gia' in archivio, alla postazione --- */
            if (pazienteEsistente) {
                const r = await api.post(
                    `/modulistica/completa-pratica/${pazienteEsistente.id}`, payload
                );
                if (!r.data.success) {
                    notificaErrore(r.data.message || 'Errore nel completamento.');
                    return;
                }
                if (onSuccess) onSuccess(r.data.data.paziente);
                onClose();
                return;
            }

            /* --- Sul tablet: la pratica va in coda, non crea nulla --- */
            if (chiaveTablet) {
                const r = await fetch('/api/firma-tablet/pratica', {
                    method: 'POST',
                    headers: intestazioniTablet(chiaveTablet, true),
                    body: JSON.stringify(payload),
                });
                const d = await r.json();
                if (!d.success) {
                    notificaErrore(d.message || 'Errore nell\'invio dei dati.');
                    return;
                }
                if (onSuccess) onSuccess(null);
                onClose();
                return;
            }

            const risposta = await modulisticaAPI.creaCompleta(payload);
            const { paziente, falliti = [] } = risposta.data.data;

            (falliti.length ? notificaAvviso : notificaConferma)(
                `Pratica completata: ${paziente.cognome} ${paziente.nome} (${paziente.id_paziente}).\n\n` +
                (falliti.length
                    ? `ATTENZIONE: moduli non archiviati: ${falliti.join(', ')}.`
                    : 'Tutti e quattro i moduli firmati sono stati archiviati.')
            );

            if (onSuccess) onSuccess(paziente);
            onClose();
        } catch (errore) {
            console.error('Errore invio pratica:', errore);
            notificaErrore(messaggioDa(errore, 'Errore durante il salvataggio della pratica.'));
        } finally {
            setInvio(false);
        }
    };

    const classe = (campo) => (errori[campo] ? 'input-modulo errore' : 'input-modulo');

    /* ---------- Render degli step ---------- */

    const renderPasso1 = () => (
        <>
            <h2 className="modulo-titolo">Modulo di registrazione anagrafica</h2>
            <p className="modulo-nota">I campi contrassegnati con * sono obbligatori.</p>

            <section className="modulo-sezione">
                <h3>Dati anagrafici</h3>
                <div className="griglia">
                    <div className="campo col-6">
                        <label>Cognome *</label>
                        <input className={classe('cognome')} value={anagrafica.cognome}
                            onChange={(e) => aggiornaAnagrafica('cognome', e.target.value)} />
                        {errori.cognome && <span className="messaggio-errore">{errori.cognome}</span>}
                    </div>
                    <div className="campo col-6">
                        <label>Nome *</label>
                        <input className={classe('nome')} value={anagrafica.nome}
                            onChange={(e) => aggiornaAnagrafica('nome', e.target.value)} />
                        {errori.nome && <span className="messaggio-errore">{errori.nome}</span>}
                    </div>
                    <div className="campo col-4">
                        <label>Sesso *</label>
                        <select className={classe('sesso')} value={anagrafica.sesso}
                            onChange={(e) => aggiornaAnagrafica('sesso', e.target.value)}>
                            <option value="">Seleziona…</option>
                            <option value="M">M</option>
                            <option value="F">F</option>
                        </select>
                        {errori.sesso && <span className="messaggio-errore">{errori.sesso}</span>}
                    </div>
                    <div className="campo col-4">
                        <label>Data di nascita *</label>
                        <CampoDataNascita className={classe('data_nascita')} valore={anagrafica.data_nascita}
                            grande={!!chiaveTablet}
                            onChange={(v) => { setDaCodice(false); aggiornaAnagrafica('data_nascita', v); }} />
                        {daCodice && anagrafica.data_nascita && (
                            <span className="modulo-avviso-riga">
                                Data di nascita e sesso letti dal codice fiscale: controlla che siano giusti.
                            </span>
                        )}
                        {errori.data_nascita && <span className="messaggio-errore">{errori.data_nascita}</span>}
                    </div>
                    <div className="campo col-4">
                        <label>Codice fiscale *</label>
                        <CampoDocumento
                            valore={anagrafica.codice_fiscale}
                            tipo={anagrafica.tipo_documento || 'cf_italiano'}
                            onChange={(v) => {
                                aggiornaAnagrafica('codice_fiscale', v);
                                cercaInArchivio(v);
                                precompilaDalCodice(v);
                            }}
                            onTipoChange={(v) => aggiornaAnagrafica('tipo_documento', v)}
                            anagrafica={{ data_nascita: anagrafica.data_nascita, sesso: anagrafica.sesso }}
                            errore={errori.codice_fiscale} />
                        {errori.codice_fiscale && <span className="messaggio-errore">{errori.codice_fiscale}</span>}

                        {giaInArchivio && giaInArchivio.anonimo && (
                            <div className="pf-gia-archivio">
                                <div>
                                    <b>Risulti già registrato. Consegna il tablet in segreteria.</b>
                                    <p>
                                        Non serve registrarsi di nuovo: da qui non si va
                                        avanti. In segreteria ritrovano la tua scheda in un
                                        momento.
                                    </p>
                                </div>
                            </div>
                        )}

                        {chiaveTablet && nonVerificato && (
                            <div className="modulo-avviso-riga" role="status">
                                Non riesco a verificare adesso il codice fiscale: prosegui, la segreteria controllerà.
                            </div>
                        )}

                        {!chiaveTablet && giaInArchivio && !giaInArchivio.anonimo && (
                            <div className="pf-gia-archivio">
                                <div>
                                    <b>Questo paziente è già in archivio</b>
                                    <p>
                                        {/* UN COLLEGAMENTO ALLA SCHEDA (Alex, 28/09/2026), solo
                                            alla postazione: questo riquadro non compare mai sul
                                            tablet, che riceve soltanto «trovato» (anonimo). Si apre
                                            in una scheda nuova, cosi' la pratica non si perde; la
                                            scheda del paziente applica i suoi permessi come da
                                            ogni altra strada. Un paziente nel cestino non ha una
                                            scheda da aprire: resta il nome. */}
                                        {!giaInArchivio.nel_cestino && giaInArchivio.id ? (
                                            <a href={`/pazienti/${giaInArchivio.id}`} target="_blank" rel="noopener noreferrer"
                                               title="Apre l'anagrafica in una scheda nuova">
                                                {giaInArchivio.cognome} {giaInArchivio.nome}
                                            </a>
                                        ) : (
                                            <>{giaInArchivio.cognome} {giaInArchivio.nome}</>
                                        )}
                                        {giaInArchivio.id_paziente ? ` · ${giaInArchivio.id_paziente}` : ''}
                                        {giaInArchivio.nel_cestino
                                            ? ' — si trova nel cestino, va recuperato da lì'
                                            : giaInArchivio.da_completare
                                                ? ' — anagrafica da completare'
                                                : ''}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="campo col-5">
                        {/* Con uno Stato scritto il campo diventa la citta' estera;
                            ma se il codice dice un comune ceduto nel 1947 resta un
                            comune italiano, e lo Stato scritto e' quello che
                            l'avviso chiede di togliere (il beta, 3/10/2026: la
                            parola cambiava accanto all'avviso che dice il
                            contrario). */}
                        <label>{String(anagrafica.stato_nascita || '').trim() && !(luogoDalCodice && luogoDalCodice.tipo === 'ceduto') ? 'Città di nascita *' : 'Comune di nascita *'}</label>
                        <RicercaComune
                            chiave={chiaveTablet}
                            valore={anagrafica.luogo_nascita}
                            codiceFiscale={anagrafica.codice_fiscale}
                            statoEstero={anagrafica.stato_nascita}
                            errore={!!errori.luogo_nascita}
                            onChange={(v) => aggiornaAnagrafica('luogo_nascita', v)}
                            onScelta={(c) => {
                                aggiornaAnagrafica('luogo_nascita', c.nome);
                                aggiornaAnagrafica('provincia_nascita', c.sigla_provincia);
                            }} />
                        {luogoDalCodice && luogoDalCodice.tipo === 'comune'
                            && anagrafica.luogo_nascita === luogoDalCodice.nome && (
                            <span className="modulo-avviso-riga">
                                Comune di nascita letto dal codice fiscale: controlla che sia giusto.
                            </span>
                        )}
                        {luogoDalCodice && luogoDalCodice.tipo === 'ceduto' && (
                            <span className="modulo-avviso-riga">
                                Il codice fiscale indica {luogoDalCodice.nome}, comune italiano fino al 1947 (provincia di {luogoDalCodice.provincia}).
                            </span>
                        )}
                        {errori.luogo_nascita && <span className="messaggio-errore">{errori.luogo_nascita}</span>}
                    </div>
                    <div className="campo col-3">
                        <label>Provincia *</label>
                        {/* La provincia di allora di un comune ceduto (PL, FU, ZA)
                            non e' fra quelle di oggi: si aggiunge all'elenco solo
                            per chi ha quel comune nel codice, cosi' si puo'
                            rimettere dopo averla tolta. */}
                        <SelezioneRicerca
                            valore={anagrafica.provincia_nascita}
                            onChange={(v) => aggiornaAnagrafica('provincia_nascita', v)}
                            opzioni={luogoDalCodice && luogoDalCodice.tipo === 'ceduto' && luogoDalCodice.sigla_provincia
                                && !PROVINCE_ITALIANE.includes(luogoDalCodice.sigla_provincia)
                                ? [...PROVINCE_ITALIANE, luogoDalCodice.sigla_provincia]
                                : PROVINCE_ITALIANE}
                            placeholder="Sigla…"
                        />
                        {errori.provincia_nascita && <span className="messaggio-errore">{errori.provincia_nascita}</span>}
                    </div>
                    <div className="campo col-4">
                        <label>Stato di nascita (se estero)</label>
                        <RicercaStato
                            chiave={chiaveTablet}
                            grande={!!chiaveTablet}
                            valore={anagrafica.stato_nascita}
                            codiceFiscale={(anagrafica.tipo_documento || 'cf_italiano') === 'cf_italiano' ? anagrafica.codice_fiscale : ''}
                            onChange={(v) => aggiornaAnagrafica('stato_nascita', v)} />
                        {luogoDalCodice && luogoDalCodice.tipo === 'stato'
                            && anagrafica.stato_nascita === luogoDalCodice.nome && (
                            <span className="modulo-avviso-riga">
                                Stato di nascita letto dal codice fiscale: controlla che sia giusto.
                            </span>
                        )}
                    </div>
                </div>
            </section>

            <section className="modulo-sezione">
                <h3>Residenza / domicilio</h3>
                <div className="griglia">
                    <div className="campo col-8">
                        <label>Via *</label>
                        <input className={classe('via')} value={anagrafica.via}
                            onChange={(e) => aggiornaAnagrafica('via', e.target.value)} />
                        {errori.via && <span className="messaggio-errore">{errori.via}</span>}
                    </div>
                    <div className="campo col-4">
                        <label>N. civico *</label>
                        <input className={classe('civico')} value={anagrafica.civico}
                            onChange={(e) => aggiornaAnagrafica('civico', e.target.value)} />
                        {errori.civico && <span className="messaggio-errore">{errori.civico}</span>}
                    </div>
                    <div className="campo col-6">
                        <label>Citta *</label>
                        <RicercaComune
                            chiave={chiaveTablet}
                            valore={anagrafica.citta}
                            errore={!!errori.citta}
                            onChange={(v) => aggiornaAnagrafica('citta', v)}
                            onScelta={(c) => {
                                aggiornaAnagrafica('citta', c.nome);
                                aggiornaAnagrafica('provincia', c.sigla_provincia);
                                if (!anagrafica.cap) aggiornaAnagrafica('cap', c.cap || '');
                            }} />
                        {errori.citta && <span className="messaggio-errore">{errori.citta}</span>}
                    </div>
                    <div className="campo col-3">
                        <label>Provincia {residenzaInItalia
                            ? '*' : <span className="facoltativo">(non richiesta)</span>}</label>
                        <SelezioneRicerca
                            valore={anagrafica.provincia}
                            onChange={(v) => aggiornaAnagrafica('provincia', v)}
                            opzioni={PROVINCE_ITALIANE}
                            placeholder="Sigla…"
                        />
                        {errori.provincia && <span className="messaggio-errore">{errori.provincia}</span>}
                    </div>
                    <div className="campo col-3">
                        <label>CAP {residenzaInItalia
                            ? '*' : <span className="facoltativo">(non richiesto)</span>}</label>
                        <input className={classe('cap')} inputMode={residenzaInItalia ? 'numeric' : 'text'}
                            maxLength={residenzaInItalia ? 5 : 12}
                            placeholder={residenzaInItalia ? '47822' : ''}
                            value={anagrafica.cap}
                            onChange={(e) => aggiornaAnagrafica('cap', e.target.value)} />
                        {errori.cap && <span className="messaggio-errore">{errori.cap}</span>}
                        <AvvisoCap cap={anagrafica.cap} comune={anagrafica.citta} chiave={chiaveTablet}
                                   attivo={residenzaInItalia} />
                    </div>
                    {/* Lo stato di residenza. Vuoto = Italia, e finche' e'
                        vuoto la schermata si comporta esattamente come
                        prima: il caso italiano non deve pagare niente per
                        il caso estero. Scrivendo uno Stato, provincia e
                        CAP smettono di essere obbligatori - a San Marino
                        la provincia non esiste. */}
                    <div className="campo col-6">
                        <label>Stato di residenza
                            <span className="facoltativo">(solo se non l&apos;Italia)</span></label>
                        <input className="input-modulo"
                            placeholder="Es. San Marino"
                            value={anagrafica.stato_residenza || ''}
                            onChange={(e) => aggiornaAnagrafica('stato_residenza', e.target.value)} />
                    </div>
                </div>
                {!residenzaInItalia && (
                    <p className="modulo-avviso-riga">
                        Residenza fuori dall&apos;Italia: provincia e CAP non sono obbligatori.
                    </p>
                )}
            </section>

            <section className="modulo-sezione">
                <h3>Recapiti</h3>
                <div className="griglia">
                    <div className="campo col-6">
                        <label>Telefono *</label>
                        <input className={classe('telefono')} inputMode="tel" value={anagrafica.telefono}
                            onChange={(e) => aggiornaAnagrafica('telefono', e.target.value)} />
                        {errori.telefono && <span className="messaggio-errore">{errori.telefono}</span>}
                    </div>
                    <div className="campo col-6">
                        <label>Email *</label>
                        <input type="email" className={classe('email')} value={anagrafica.email}
                            onChange={(e) => aggiornaAnagrafica('email', e.target.value)} />
                        {errori.email && <span className="messaggio-errore">{errori.email}</span>}
                    </div>
                </div>
            </section>

            <section className="modulo-sezione">
                <h3>Dichiarazione</h3>
                <p className="testo-dichiarazione">
                    Il/La sottoscritto/a dichiara che i dati sopra riportati sono veritieri e completi, e si impegna
                    a comunicare tempestivamente alla struttura ogni successiva variazione, in particolare dei
                    recapiti telefonici e di posta elettronica utilizzati per le comunicazioni relative alle
                    prestazioni sanitarie. Dichiara inoltre di aver ricevuto e preso visione dell'informativa sul
                    trattamento dei dati personali resa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679.
                </p>
                <label className="riga-checkbox">
                    <input type="checkbox" checked={dichiarazione}
                        onChange={(e) => { setDichiarazione(e.target.checked); pulisciErrore('dichiarazione'); }} />
                    <span>Ho letto e confermo quanto sopra dichiarato *</span>
                </label>
                {errori.dichiarazione && <span className="messaggio-errore">{errori.dichiarazione}</span>}
            </section>

            {/* Un minore non firma: firma chi ne esercita la
                responsabilita' genitoriale, e sul modulo deve risultare
                chi. Una firma senza un nome accanto non dice a nessuno chi
                ha acconsentito, e fra sei mesi non e' ricostruibile. */}
            {minore && (
                <section className="modulo-sezione riquadro-minore">
                    <h3>Paziente minorenne — firma chi ne esercita la responsabilità genitoriale</h3>
                    <p className="nota-minore">
                        Il paziente ha {anni} {anni === 1 ? 'anno' : 'anni'}: i moduli
                        e i consensi vanno firmati dal genitore o dal tutore, i cui dati
                        compariranno accanto alla firma su tutti e quattro i documenti.
                    </p>
                    <div className="griglia sotto-campi">
                        <div className="campo col-4">
                            <label>Cognome di chi firma *</label>
                            <input className="input-modulo" value={firmatario.cognome}
                                onChange={(e) => { setFirmatario((f) => ({ ...f, cognome: e.target.value })); pulisciErrore('firmatario'); }} />
                        </div>
                        <div className="campo col-4">
                            <label>Nome di chi firma *</label>
                            <input className="input-modulo" value={firmatario.nome}
                                onChange={(e) => { setFirmatario((f) => ({ ...f, nome: e.target.value })); pulisciErrore('firmatario'); }} />
                        </div>
                        <div className="campo col-4">
                            <label>Codice fiscale <span className="facoltativo">(se disponibile)</span></label>
                            <input className="input-modulo" value={firmatario.codice_fiscale}
                                onChange={(e) => setFirmatario((f) => ({ ...f, codice_fiscale: e.target.value.toUpperCase().replace(/\s/g, '') }))} />
                        </div>
                        <div className="campo col-4">
                            <label>In qualità di *</label>
                            <select className="input-modulo" value={firmatario.relazione}
                                onChange={(e) => { setFirmatario((f) => ({ ...f, relazione: e.target.value })); pulisciErrore('firmatario'); }}>
                                {RELAZIONI.map((r) => (
                                    <option key={r.valore} value={r.valore}>{r.etichetta}</option>
                                ))}
                            </select>
                        </div>
                        {firmatario.relazione === 'altro' && (
                            <div className="campo col-8">
                                <label>Specificare *</label>
                                <input className="input-modulo" value={firmatario.altra_relazione}
                                    onChange={(e) => { setFirmatario((f) => ({ ...f, altra_relazione: e.target.value })); pulisciErrore('firmatario'); }} />
                            </div>
                        )}
                    </div>
                    {errori.firmatario && <span className="messaggio-errore">{errori.firmatario}</span>}
                </section>
            )}

            <section className="modulo-sezione">
                <h3>{minore
                    ? 'Firma di chi esercita la responsabilità genitoriale'
                    : 'Firma del paziente'}</h3>
                <FirmaCanvas
                    key="firma-anagrafica"
                    etichetta={etichettaFirma
                        ? etichettaFirma + ' — Santarcangelo di Romagna, '
                          + new Date().toLocaleDateString('it-IT')
                        : undefined}
                    valore={firme.anagrafica}
                    onChange={(v) => impostaFirma('anagrafica', v)}
                    errore={errori.firma_anagrafica}
                />
            </section>
        </>
    );

    const renderPasso2 = () => (
        <>
            <h2 className="modulo-titolo">Consenso al trattamento dei dati personali</h2>
            <p className="modulo-nota">artt. 13 e 14 Regolamento UE 2016/679 — GDPR</p>

            <section className="modulo-sezione">
                <RiepilogoPaziente a={anagrafica} />
            </section>

            <section className="modulo-sezione">
                <h3>Informativa</h3>
                {/* IL TESTO VIENE DA UN POSTO SOLO (23 settembre 2026).
                    Qui c'era un riassunto scritto a mano, e nel PDF il testo
                    per esteso: due copie, gia' divergenti - nel PDF c'era
                    una finalita' in piu', «Difesa in giudizio», che qui non
                    compariva - e il PDF si chiude con «dichiara di aver
                    letto e compreso l'informativa che precede».
                    Adesso e' parola per parola lo stesso testo: la fonte e'
                    `backend/services/informativaPrivacy.js`, e questa copia
                    la riscrive il build. */}
                <div className="testo-informativa">
                    {informativaPrivacy({ titolareEsteso: titolare }).map((s) => (
                        <div key={s.titolo}>
                            <h4>{s.titolo}</h4>
                            {(s.elenco || []).length > 0 && (
                                <ul>{s.elenco.map((v, k) => <li key={k}>{v}</li>)}</ul>
                            )}
                            {(s.paragrafi || []).map((v, k) => <p key={k}>{v}</p>)}
                        </div>
                    ))}
                </div>
            </section>

            <section className="modulo-sezione">
                <h3>Le tue scelte</h3>

                {!soloPrelievi && (
                    <VoceConsenso
                        titolo="A. Trattamento dei dati sulla salute per diagnosi, cura e cartella clinica"
                        descrizione="Necessario per erogare la prestazione."
                        valore={privacy.cura}
                        onChange={(v) => { setPrivacy((p) => ({ ...p, cura: v })); pulisciErrore('cura'); }}
                        errore={errori.cura}
                    />
                )}

                <VoceConsenso
                    titolo="B. Comunicazione dello stato di salute a familiari o persona di fiducia"
                    valore={privacy.familiari}
                    onChange={(v) => { setPrivacy((p) => ({ ...p, familiari: v })); pulisciErrore('familiari'); }}
                    errore={errori.familiari}
                >
                    {privacy.familiari === true && (
                        <>
                            <div className="griglia sotto-campi">
                                <div className="campo col-4">
                                    <label>Prima persona designata *</label>
                                    <input className="input-modulo" value={privacy.familiare_nome}
                                        onChange={(e) => { setPrivacy((p) => ({ ...p, familiare_nome: e.target.value })); pulisciErrore('familiari'); }} />
                                </div>
                                <div className="campo col-4">
                                    <label>Relazione</label>
                                    <input className="input-modulo" value={privacy.familiare_relazione}
                                        onChange={(e) => setPrivacy((p) => ({ ...p, familiare_relazione: e.target.value }))} />
                                </div>
                                <div className="campo col-4">
                                    <label>Recapito</label>
                                    <input className="input-modulo" value={privacy.familiare_recapito}
                                        onChange={(e) => setPrivacy((p) => ({ ...p, familiare_recapito: e.target.value }))} />
                                </div>
                            </div>
                            {/* La seconda e' facoltativa e lo dice: chi ne
                                designa una sola non deve chiedersi se ha
                                lasciato un campo obbligatorio in bianco. */}
                            <div className="griglia sotto-campi">
                                <div className="campo col-4">
                                    <label>Seconda persona designata <span className="facoltativo">(facoltativa)</span></label>
                                    <input className="input-modulo" value={privacy.familiare2_nome}
                                        onChange={(e) => setPrivacy((p) => ({ ...p, familiare2_nome: e.target.value }))} />
                                </div>
                                <div className="campo col-4">
                                    <label>Relazione</label>
                                    <input className="input-modulo" value={privacy.familiare2_relazione}
                                        onChange={(e) => setPrivacy((p) => ({ ...p, familiare2_relazione: e.target.value }))} />
                                </div>
                                <div className="campo col-4">
                                    <label>Recapito</label>
                                    <input className="input-modulo" value={privacy.familiare2_recapito}
                                        onChange={(e) => setPrivacy((p) => ({ ...p, familiare2_recapito: e.target.value }))} />
                                </div>
                            </div>
                        </>
                    )}
                </VoceConsenso>

                {/* IL MEDICO CURANTE NON STA DENTRO IL CONSENSO B.
                    Dentro, i campi comparirebbero solo a chi acconsente a
                    far comunicare il proprio stato di salute a un
                    familiare - e chi nega quel consenso ha comunque un
                    medico curante. E' un dato, non una scelta. */}
                <div className="modulo-riquadro">
                    <h4>Medico curante</h4>
                    <p className="modulo-riquadro-aiuto">
                        Il medico di medicina generale del paziente.
                    </p>
                    <label className="spunta-riga">
                        <input type="checkbox"
                            checked={privacy.medico_curante_assente}
                            onChange={(e) => {
                                const assente = e.target.checked;
                                setPrivacy((p) => ({
                                    ...p,
                                    medico_curante_assente: assente,
                                    // Spuntando, i campi si svuotano: un nome
                                    // rimasto sotto una spunta «non ne ha»
                                    // e' un dato che si contraddice.
                                    medico_curante: assente ? '' : p.medico_curante,
                                    medico_curante_contatto: assente ? '' : p.medico_curante_contatto,
                                }));
                                pulisciErrore('medico_curante');
                            }} />
                        <span>Il paziente dichiara di non avere un medico curante</span>
                    </label>

                    {!privacy.medico_curante_assente && (
                        <div className="griglia sotto-campi">
                            <div className="campo col-6">
                                <label>Nome e cognome *</label>
                                <input className={errori.medico_curante
                                        ? 'input-modulo errore' : 'input-modulo'}
                                    value={privacy.medico_curante}
                                    onChange={(e) => {
                                        setPrivacy((p) => ({ ...p, medico_curante: e.target.value }));
                                        pulisciErrore('medico_curante');
                                    }} />
                            </div>
                            <div className="campo col-6">
                                <label>Contatto <span className="facoltativo">(facoltativo)</span></label>
                                <input className="input-modulo"
                                    placeholder="Telefono o email"
                                    value={privacy.medico_curante_contatto}
                                    onChange={(e) => setPrivacy((p) => ({
                                        ...p, medico_curante_contatto: e.target.value }))} />
                            </div>
                        </div>
                    )}
                    {errori.medico_curante
                        && <span className="messaggio-errore">{errori.medico_curante}</span>}
                </div>

                <VoceConsenso
                    titolo="C. Promemoria e comunicazioni organizzative sugli appuntamenti"
                    descrizione="SMS, email o messaggistica."
                    valore={privacy.promemoria}
                    onChange={(v) => { setPrivacy((p) => ({ ...p, promemoria: v })); pulisciErrore('promemoria'); }}
                    errore={errori.promemoria}
                />

                <VoceConsenso
                    titolo="D. Trasmissione di referti e documentazione sanitaria via email"
                    valore={privacy.referti_email}
                    onChange={(v) => { setPrivacy((p) => ({ ...p, referti_email: v })); pulisciErrore('referti_email'); }}
                    errore={errori.referti_email}
                />

                <VoceConsenso
                    titolo="E. Comunicazioni promozionali e informazioni su nuovi servizi"
                    valore={privacy.marketing}
                    onChange={(v) => { setPrivacy((p) => ({ ...p, marketing: v })); pulisciErrore('marketing'); }}
                    errore={errori.marketing}
                />
            </section>

            {/* Sezione separata, non una voce A-E: la trasmissione al Sistema
                TS e' un obbligo di legge, non un consenso, e quello che si
                raccoglie qui e' l'eventuale opposizione del paziente. Tenerla
                fra i consensi avrebbe confuso i due significati. */}
            <section className="modulo-sezione">
                <h3>Sistema Tessera Sanitaria</h3>
                <p className="modulo-nota">
                    Le spese sanitarie sono trasmesse al Sistema Tessera Sanitaria per la
                    dichiarazione precompilata, per obbligo di legge. Il paziente può opporsi:
                    l&apos;opposizione non incide sulle prestazioni e si può revocare in
                    qualsiasi momento.
                </p>
                <VoceConsenso
                    titolo="Opposizione alla trasmissione delle spese sanitarie"
                    descrizione="Senza opposizione i dati vengono trasmessi, come previsto dalla legge."
                    valore={privacy.opposizione_ts}
                    onChange={(v) => { setPrivacy((p) => ({ ...p, opposizione_ts: v })); pulisciErrore('opposizione_ts'); }}
                    etichette={{ si: 'Mi oppongo', no: 'Nessuna opposizione' }}
                    errore={errori.opposizione_ts}
                    neutro
                >
                    {/* IL CAMBIO SI VEDE PRIMA DELLA FIRMA (Alex, 4/10/2026).
                        Solo per un paziente gia' in archivio, e solo se la
                        scelta e' diversa da quella della scheda: e' la scelta
                        che la firma qui sotto scrivera' sulla scheda. Al
                        tablet la legge il paziente della sessione, ed e' la
                        sua: nessun dato di un altro. */}
                    {typeof oppostoSullaScheda === 'boolean'
                        && typeof privacy.opposizione_ts === 'boolean'
                        && privacy.opposizione_ts !== oppostoSullaScheda && (
                        <p className="rm-messaggio rm-messaggio-attenzione cambio-opposizione-ts"
                           role="status" data-cambio="opposizione-ts">
                            {oppostoSullaScheda ? MSG_OPPOSIZIONE_TOLTA : MSG_OPPOSIZIONE_AGGIUNTA}
                        </p>
                    )}
                </VoceConsenso>
            </section>

            <section className="modulo-sezione">
                <h3>{minore
                    ? 'Firma di chi esercita la responsabilità genitoriale'
                    : 'Firma del paziente'}</h3>
                <FirmaCanvas
                    key="firma-privacy"
                    etichetta={etichettaFirma
                        ? etichettaFirma + ' — Santarcangelo di Romagna, '
                          + new Date().toLocaleDateString('it-IT')
                        : undefined}
                    valore={firme.privacy}
                    onChange={(v) => impostaFirma('privacy', v)}
                    errore={errori.firma_privacy}
                />
            </section>
        </>
    );

    const renderPasso3 = () => (
        <>
            <h2 className="modulo-titolo">Consenso al trattamento di immagini fotografiche e video</h2>

            <section className="modulo-sezione">
                <RiepilogoPaziente a={anagrafica} />
            </section>

            <section className="modulo-sezione">
                <h3>Informazioni preliminari</h3>
                <div className="testo-informativa">
                    <p>
                        Nell'ambito dell'attività della struttura può risultare utile o necessaria l'acquisizione di
                        immagini fotografiche o video, ai fini della documentazione dello stato clinico prima,
                        durante e dopo i trattamenti.
                    </p>
                    <p>
                        Le immagini costituiscono dato personale e, quando riferite allo stato di salute, categoria
                        particolare di dati ai sensi dell'art. 9 GDPR. Il trattamento è inoltre soggetto agli artt.
                        96 e 97 della Legge 633/1941 sul diritto all'immagine.
                    </p>
                    <p>
                        Il consenso è facoltativo, gratuito e revocabile in qualsiasi momento senza motivazione e
                        senza conseguenze sull'accesso alle prestazioni.
                    </p>
                    {/* GLI ANNI NON SI SCRIVONO QUI. Erano «non oltre 10 anni»
                        a mano, mentre il PDF li prendeva dal parametro
                        `foto_anni_conservazione`: oggi coincidono perche' il
                        parametro non e' impostato e il ripiego e' 10, ma su
                        un'installazione con un valore diverso il paziente
                        accetterebbe un numero e firmerebbe l'altro. */}
                    <p>
                        {conservazioneFoto({ anniConservazioneFoto: anniFoto })}
                    </p>
                </div>
            </section>

            <section className="modulo-sezione">
                <h3>Le tue scelte</h3>

                <VoceConsenso
                    titolo="A. Documentazione clinica interna"
                    descrizione="Conservazione nella cartella clinica per la valutazione dell'evoluzione del quadro clinico e il confronto pre/post trattamento."
                    valore={foto.clinica}
                    onChange={(v) => { setFoto((p) => ({ ...p, clinica: v })); pulisciErrore('clinica'); }}
                    errore={errori.clinica}
                />

                <VoceConsenso
                    titolo="B. Finalità didattiche e scientifiche"
                    descrizione="Impiego in forma anonima e con oscuramento dei tratti identificativi in congressi, corsi e pubblicazioni scientifiche."
                    valore={foto.didattica}
                    onChange={(v) => { setFoto((p) => ({ ...p, didattica: v })); pulisciErrore('didattica'); }}
                    errore={errori.didattica}
                />

                <VoceConsenso
                    titolo="C. Finalità divulgative e promozionali"
                    descrizione="Pubblicazione sul sito della struttura, sui profili social e su materiale informativo, nel rispetto delle norme sulla pubblicità sanitaria."
                    valore={foto.promozionale}
                    onChange={(v) => { setFoto((p) => ({ ...p, promozionale: v })); pulisciErrore('promozionale'); }}
                    errore={errori.promozionale}
                >
                    {foto.promozionale === true && (
                        <div className="sotto-campi">
                            <label className="riga-radio">
                                <input type="radio" checked={foto.modalita === 'volto_oscurato'}
                                    onChange={() => setFoto((p) => ({ ...p, modalita: 'volto_oscurato' }))} />
                                <span>Solo con volto oscurato o inquadratura parziale dell'area trattata</span>
                            </label>
                            <label className="riga-radio">
                                <input type="radio" checked={foto.modalita === 'volto_riconoscibile'}
                                    onChange={() => setFoto((p) => ({ ...p, modalita: 'volto_riconoscibile' }))} />
                                <span>Con volto riconoscibile</span>
                            </label>
                        </div>
                    )}
                </VoceConsenso>
            </section>

            <section className="modulo-sezione">
                <h3>{minore
                    ? 'Firma di chi esercita la responsabilità genitoriale'
                    : 'Firma del paziente'}</h3>
                <FirmaCanvas
                    key="firma-foto"
                    etichetta={etichettaFirma
                        ? etichettaFirma + ' — Santarcangelo di Romagna, '
                          + new Date().toLocaleDateString('it-IT')
                        : undefined}
                    valore={firme.foto}
                    onChange={(v) => impostaFirma('foto', v)}
                    errore={errori.firma_foto}
                />
            </section>
        </>
    );

    const renderPasso4 = () => (
        <>
            <h2 className="modulo-titolo">Consenso all'utilizzo della Firma Elettronica Avanzata</h2>
            <p className="modulo-nota">D.Lgs. 82/2005 — Reg. UE 910/2014 — DPCM 22 febbraio 2013</p>

            <section className="modulo-sezione">
                <RiepilogoPaziente a={anagrafica} />
            </section>

            <section className="modulo-sezione">
                <h3>Informativa</h3>
                <div className="testo-informativa">
                    <p>
                        La struttura mette a disposizione una soluzione di <strong>firma elettronica avanzata di tipo
                        grafometrico</strong>: la sottoscrizione viene apposta su un dispositivo di acquisizione
                        collegato al gestionale, che registra anche i dati biometrici del gesto grafico (pressione,
                        velocità, accelerazione, ritmo).
                    </p>
                    <p>
                        I dati biometrici sono cifrati al momento dell'acquisizione e incorporati nel documento. La
                        chiave privata di decifratura è detenuta esclusivamente da un soggetto terzo fiduciario: la
                        struttura non può accedervi. Possono essere decifrati solo su richiesta dell'Autorità
                        Giudiziaria per verificare l'autenticità della sottoscrizione.
                    </p>
                    <p>
                        La struttura conserva copia del documento di identità, consegna su richiesta copia dei
                        documenti sottoscritti, conserva per venti anni la presente dichiarazione ed è coperta da
                        polizza RC verso terzi con massimale non inferiore a 500.000 euro.
                    </p>
                    <p>
                        È sempre possibile <strong>rifiutare</strong> la firma elettronica e chiedere la sottoscrizione
                        cartacea, senza alcuna conseguenza sull'erogazione delle prestazioni.
                    </p>
                </div>
            </section>

            <section className="modulo-sezione">
                <h3>Accettazione</h3>
                <VoceConsenso
                    titolo="Utilizzo della firma elettronica avanzata per la modulistica sanitaria e i consensi informati"
                    valore={fea.accetta}
                    onChange={(v) => { setFea((p) => ({ ...p, accetta: v })); pulisciErrore('accetta'); }}
                    errore={errori.accetta}
                />
            </section>

            {fea.accetta === true && (
                <section className="modulo-sezione">
                    <h3>Documento di identità</h3>
                    <div className="griglia">
                        <div className="campo col-4">
                            <label>Tipo documento *</label>
                            <select className="input-modulo" value={fea.documento.tipo}
                                onChange={(e) => aggiornaDocumento('tipo', e.target.value)}>
                                <option value="">Seleziona…</option>
                                <option value="Carta d'identita">Carta d'identità</option>
                                <option value="Patente di guida">Patente di guida</option>
                                <option value="Passaporto">Passaporto</option>
                            </select>
                        </div>
                        <div className="campo col-4">
                            <label>Numero *</label>
                            <input className="input-modulo" value={fea.documento.numero}
                                onChange={(e) => aggiornaDocumento('numero', e.target.value)} />
                        </div>
                        <div className="campo col-4">
                            <label>Rilasciato da</label>
                            <input className="input-modulo" value={fea.documento.rilasciato_da}
                                onChange={(e) => aggiornaDocumento('rilasciato_da', e.target.value)} />
                        </div>
                        <div className="campo col-4">
                            <label>Data di rilascio</label>
                            <input type="date" className="input-modulo" value={fea.documento.data_rilascio}
                                onChange={(e) => aggiornaDocumento('data_rilascio', e.target.value)} />
                        </div>
                    </div>
                    {errori.documento && <span className="messaggio-errore">{errori.documento}</span>}
                </section>
            )}

            <section className="modulo-sezione">
                <h3>{minore
                    ? 'Firma di chi esercita la responsabilità genitoriale'
                    : 'Firma del firmatario'}</h3>
                <FirmaCanvas
                    key="firma-fea"
                    etichetta={etichettaFirma
                        ? etichettaFirma + ' — Santarcangelo di Romagna, '
                          + new Date().toLocaleDateString('it-IT')
                        : undefined}
                    valore={firme.fea}
                    onChange={(v) => impostaFirma('fea', v)}
                    errore={errori.firma_fea}
                />
            </section>
        </>
    );

    const renderPasso = () => {
        if (passo === 1) return renderPasso1();
        if (passo === 2) return renderPasso2();
        if (passo === 3) return renderPasso3();
        return renderPasso4();
    };

    /* ---------- Render ---------- */

    return (
        <div className={`modulo-overlay ${chiaveTablet ? 'wizard--tablet' : ''}`}>
            <div className="modulo-finestra wizard" role="dialog" aria-modal="true"
                 aria-label="Accettazione paziente">

                <button className="modulo-chiudi" onClick={chiudi} aria-label="Chiudi">✕</button>

                <div className="modulo-body">
                    <header className="modulo-intestazione">
                        <div className="intestazione-ragione">RIMEDICAL S.r.l.</div>
                        <div className="intestazione-righe">
                            Via Europa 8 – 47822 Santarcangelo di Romagna (RN)<br />
                            Tel. 0541 620757 – Cell. 352 0453011<br />
                            P.IVA 04538930407 – Cod. Univoco KRRH6B9<br />
                            www.rimedical.it – rimedical@rimedical.it
                        </div>
                    </header>

                    <nav className="stepper" aria-label="Avanzamento">
                        {PASSI.filter((p) => passiAttivi.includes(p.n)).map((p) => (
                            <div key={p.n}
                                 className={`passo ${passo === p.n ? 'attivo' : ''} ${passo > p.n ? 'completato' : ''}`}>
                                <span className="passo-numero">{passo > p.n ? '✓' : p.n}</span>
                                <span className="passo-etichetta">{p.etichetta}</span>
                            </div>
                        ))}
                    </nav>

                    {renderPasso()}
                </div>

                <footer className="modulo-footer">
                    {passo > 1 && (
                        <button className="btn-annulla" onClick={indietro} disabled={invio}>
                            Indietro
                        </button>
                    )}
                    <button className="btn-annulla" onClick={chiudi} disabled={invio}>
                        Annulla
                    </button>
                    {passo < ultimoPasso ? (
                        <button className="btn-conferma" onClick={avanti}>
                            Avanti
                        </button>
                    ) : (
                        <button className="btn-conferma" onClick={conferma} disabled={invio}>
                            {invio ? 'Salvataggio…' : 'Completa accettazione'}
                        </button>
                    )}
                </footer>

            </div>
        </div>
    );
};

export default ModulisticaWizard;
